// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Button, Popover, Stack, StackProps, styled } from '@mui/material';
import { FormEventHandler, PropsWithChildren, ReactNode } from 'react';

const FormButton = styled(Button)(({ theme }) => ({
  minWidth: 0,
  padding: theme.spacing(0.4, 1),
}));

interface Props extends Pick<StackProps, 'width'> {
  /**
   * The element to which to anchor the popover.
   */
  anchorElement: HTMLElement | null;

  /**
   * If true, the popover is open.
   */
  isOpen: boolean;

  /**
   * If provided, this React node will be rendered at the bottom of the form.
   */
  alert?: ReactNode;

  /**
   * The position of the popover relative to the anchor element.
   */
  position: 'left' | 'right';

  /**
   * The vertical position of the popover relative to the anchor element.
   *
   * @default 'bottom'
   */
  verticalPosition?: 'top' | 'bottom';

  /**
   * The vertical offset of the popover relative to the anchor element.
   *
   * @default 0
   */
  verticalOffset?: number;

  /**
   * The horizontal offset of the popover relative to the anchor element.
   *
   * @default 0
   */
  horizontalOffset?: number;

  /**
   * A callback fired when the form is submitted.
   */
  onSubmit: FormEventHandler<HTMLFormElement>;

  /**
   * A callback fired when the popover is closed.
   */
  onCancel: () => void;

  /**
   * The label for the submit button.
   *
   * @default 'Save'
   */
  submitLabel?: string;
}

/**
 * A popover that contains a form with a cancel and save button.
 */
export function PopoverForm(props: PropsWithChildren<Props>) {
  const vertical = props.verticalPosition ?? 'bottom';
  const verticalOffset = props.verticalOffset ?? 0;
  const horizontalOffset = props.horizontalOffset ?? 0;

  return (
    <Popover
      open={props.isOpen}
      onClose={props.onCancel}
      anchorEl={props.anchorElement}
      anchorOrigin={{ vertical, horizontal: props.position }}
      transformOrigin={{ vertical: 'top', horizontal: props.position }}
      slotProps={{
        paper: {
          sx: {
            marginTop: `${verticalOffset}px`,
            marginLeft: `${horizontalOffset}px`,
          },
        },
      }}
    >
      <Stack
        component="form"
        width={props.width}
        padding={1}
        spacing={1}
        onSubmit={props.onSubmit}
      >
        {props.children}
        <Stack
          useFlexGap
          spacing={1}
          direction="row"
          alignItems="center"
          paddingLeft={0.5}
        >
          {props.alert}
          <FormButton onClick={props.onCancel} sx={{ marginLeft: 'auto' }}>
            Cancel
          </FormButton>
          <FormButton type="submit" color="secondary" variant="contained">
            {props.submitLabel ?? 'Save'}
          </FormButton>
        </Stack>
      </Stack>
    </Popover>
  );
}
